import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { tr } from "@/utils/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Gift, Copy, Mail, Users, CalendarPlus, Crown, Trophy, MessageCircle, Share2, Lock } from "lucide-react";

const MILESTONE_TITLES = {
  1: ["First Friend", "Primeiro Amigo", "Primer Amigo"],
  3: ["Connector", "Conector", "Conector"],
  5: ["Ambassador", "Embaixador", "Embajador"],
  10: ["Legend", "Lenda", "Leyenda"],
};
const milestoneTitle = (n, language) => tr(language, ...(MILESTONE_TITLES[n] || [`${n}`, `${n}`, `${n}`]));

function StatTile({ value, label, color, icon: Icon, testId }) {
  return (
    <div className="p-4 bg-zinc-900/60 rounded-xl border border-zinc-800">
      <Icon className={`w-5 h-5 mb-2 ${color}`} />
      <p className={`text-3xl font-bold ${color}`} data-testid={testId}>{value}</p>
      <p className="text-xs text-zinc-500 mt-1">{label}</p>
    </div>
  );
}

function MilestoneProgress({ stats, language }) {
  const joined = stats.successful_referrals || 0;
  const next = stats.next_milestone;
  const pct = next ? Math.round((joined / next.friends) * 100) : 100;
  const remaining = next ? next.friends - joined : 0;
  const nextTitle = next ? milestoneTitle(next.friends, language) : "";
  const friendWord = (n) => (n === 1 ? tr(language, "friend", "amigo", "amigo") : tr(language, "friends", "amigos", "amigos"));
  return (
    <div className="space-y-4" data-testid="referral-milestones">
      <div className="flex items-center justify-between text-sm">
        <span className="text-zinc-300 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-400" />
          {next
            ? tr(language,
              `${remaining} more ${friendWord(remaining)} to reach ${nextTitle}`,
              `Faltam ${remaining} ${friendWord(remaining)} para ${nextTitle}`,
              `Faltan ${remaining} ${friendWord(remaining)} para ${nextTitle}`)
            : tr(language, "All badges unlocked!", "Todas as conquistas desbloqueadas!", "¡Todas las insignias desbloqueadas!")}
        </span>
        <span className="text-zinc-500 text-xs">{joined}/{next ? next.friends : joined}</span>
      </div>
      <Progress value={pct} className="h-2 bg-zinc-800" data-testid="referral-progress" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {(stats.milestones || []).map((m) => (
          <div key={m.friends} data-testid={`milestone-${m.friends}`}
            className={`p-3 rounded-lg border text-center transition-colors ${m.reached ? "border-amber-500/50 bg-amber-500/10" : "border-zinc-800 bg-zinc-900/40"}`}>
            {m.reached ? <Trophy className="w-4 h-4 mx-auto text-amber-400" /> : <Lock className="w-4 h-4 mx-auto text-zinc-600" />}
            <p className={`text-xs font-semibold mt-1 ${m.reached ? "text-amber-300" : "text-zinc-500"}`}>{milestoneTitle(m.friends, language)}</p>
            <p className="text-[10px] text-zinc-500">{m.friends} {friendWord(m.friends)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ShareRow({ stats, language, copyReferralLink }) {
  const link = stats.referral_link || "";
  const message = tr(language,
    `I plan my peptide cycles with PlanMyPeptides. Join with my link and we both get 7 days of Premium free: ${link}`,
    `Eu planejo meus ciclos de peptídeos com o PlanMyPeptides. Entre pelo meu link e ganhamos 7 dias de Premium grátis: ${link}`,
    `Planifico mis ciclos de péptidos con PlanMyPeptides. Únete con mi enlace y ambos obtenemos 7 días de Premium gratis: ${link}`);
  const nativeShare = () => navigator.share?.({ title: "PlanMyPeptides", text: message, url: link }).catch(() => {});
  return (
    <div className="space-y-2">
      <Label className="text-zinc-400">{tr(language, "Your referral link", "Seu link de indicação", "Tu enlace de referencia")}</Label>
      <div className="flex gap-2">
        <Input value={link} readOnly className="bg-zinc-900 border-zinc-700 text-white" data-testid="referral-link-input" />
        <Button onClick={copyReferralLink} variant="outline" className="border-cyan-500/50" data-testid="copy-referral-link">
          <Copy className="w-4 h-4" />
        </Button>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <Button asChild className="bg-green-600 hover:bg-green-500 text-white" data-testid="share-whatsapp">
          <a href={`https://wa.me/?text=${encodeURIComponent(message)}`} target="_blank" rel="noopener noreferrer">
            <MessageCircle className="w-4 h-4 mr-2" />WhatsApp
          </a>
        </Button>
        {typeof navigator !== "undefined" && navigator.share && (
          <Button variant="outline" onClick={nativeShare} data-testid="share-native">
            <Share2 className="w-4 h-4 mr-2" />{tr(language, "Share", "Compartilhar", "Compartir")}
          </Button>
        )}
      </div>
    </div>
  );
}

function FriendList({ referrals, language }) {
  if (!referrals?.length) {
    return (
      <p className="text-sm text-zinc-500 text-center py-6" data-testid="referral-empty">
        {tr(language, "No friends yet. Share your link to earn your first 7 days!", "Nenhum amigo ainda. Compartilhe seu link para ganhar seus primeiros 7 dias!", "Aún no hay amigos. ¡Comparte tu enlace para ganar tus primeros 7 días!")}
      </p>
    );
  }
  return (
    <div className="space-y-2" data-testid="referral-history">
      {referrals.map((ref, i) => (
        <div key={`${ref.display_name}-${ref.created_at || i}`} className="flex items-center justify-between p-3 bg-zinc-900/50 rounded-lg">
          <div>
            <span className="text-sm text-white">{ref.display_name}</span>
            <span className="block text-xs text-zinc-500">{ref.created_at ? new Date(ref.created_at).toLocaleDateString() : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            {ref.is_premium && <Badge className="bg-yellow-500/20 text-yellow-300"><Crown className="w-3 h-3 mr-1" />Premium</Badge>}
            <Badge className={ref.status === "completed" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"}>
              {ref.status === "completed" ? `+${ref.reward_days} ${tr(language, "days", "dias", "días")}` : tr(language, "Pending", "Pendente", "Pendiente")}
            </Badge>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReferralsTab({ referralStats, language, inviteEmail, setInviteEmail, sendingInvite, handleSendInvite, copyReferralLink }) {
  const s = referralStats;
  return (
    <div className="space-y-6" data-testid="referrals-content">
      <Card className="bg-gradient-to-br from-green-950/30 to-zinc-950 border-green-500/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-white">
            <Gift className="w-5 h-5 text-green-400" />
            {tr(language, "Referral Rewards", "Recompensas de Indicação", "Recompensas por Referencia")}
          </CardTitle>
          <p className="text-sm text-zinc-400">
            {tr(language, "Every friend who joins with your link gives you AND them 7 days of Premium free.", "Cada amigo que entra pelo seu link dá a você E a ele 7 dias de Premium grátis.", "Cada amigo que se une con tu enlace te da a ti Y a él 7 días de Premium gratis.")}
          </p>
        </CardHeader>
        {s && (
          <CardContent className="space-y-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={Users} color="text-white" value={s.successful_referrals} testId="referral-friends-joined"
                label={tr(language, "Friends joined", "Amigos que entraram", "Amigos unidos")} />
              <StatTile icon={Gift} color="text-green-400" value={s.lifetime_days_earned ?? 0} testId="referral-days-earned"
                label={tr(language, "Premium days earned", "Dias Premium ganhos", "Días Premium ganados")} />
              <StatTile icon={CalendarPlus} color="text-cyan-400" value={s.bonus_days_earned ?? 0} testId="referral-days-available"
                label={tr(language, "Days ready to use", "Dias para usar", "Días para usar")} />
              <StatTile icon={Crown} color="text-yellow-300" value={s.premium_friends ?? 0} testId="referral-premium-friends"
                label={tr(language, "Friends on Premium", "Amigos no Premium", "Amigos en Premium")} />
            </div>
            {s.bonus_days_earned > 0 && (
              <p className="text-xs text-cyan-300/80 -mt-4" data-testid="referral-days-hint">
                {tr(language, "Your ready-to-use days are added automatically when you subscribe.", "Seus dias são adicionados automaticamente quando você assina.", "Tus días se añaden automáticamente al suscribirte.")}
              </p>
            )}
            <MilestoneProgress stats={s} language={language} />
            <ShareRow stats={s} language={language} copyReferralLink={copyReferralLink} />
            <div className="space-y-2">
              <Label className="text-zinc-400">{tr(language, "Invite by email", "Convidar por email", "Invitar por email")}</Label>
              <div className="flex gap-2">
                <Input type="email" placeholder="friend@email.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="bg-zinc-900 border-zinc-700 text-white" data-testid="invite-email-input" />
                <Button onClick={handleSendInvite} disabled={!inviteEmail || sendingInvite} className="btn-primary" data-testid="send-invite-btn">
                  {sendingInvite ? <div className="spinner w-4 h-4" /> : <Mail className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-400">{tr(language, "Friends who joined", "Amigos que entraram", "Amigos que se unieron")}</Label>
              <FriendList referrals={s.referrals} language={language} />
            </div>
          </CardContent>
        )}
      </Card>
    </div>
  );
}
